import React, { Component } from "react";

import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ProBadge from "js/react/components/ProBadge";
import {
    Divider,
    UIContainer,
    UINavigation,
    UINavigationItem
} from "js/react/components/UiComponents";
import Analytics from "js/react/views/Analytics/Analytics";
import AnalyticsController from "js/react/views/Analytics/AnalyticsController";

import CollaboratePane from "./panes/CollaboratePane";
import ExportPane from "./panes/ExportPane";
import PlayerSettingsPane from "./panes/PlayerSettingsPane";
import SharePane from "./panes/SharePane";
import VersionHistoryPane from "./panes/VersionHistoryPane";
import PropertiesPane from "./panes/PropertiesPane";

import "css/presentation-settings.scss";
import { t } from "js/i18n";
function trackAnalytics(id) {
    switch (id) {
        case "share":
            trackActivity("PresentationActions", "ShowShare");
            break;
        case "collaborate":
            trackActivity("PresentationActions", "ShowCollaborate");
            break;
        case "export":
            trackActivity("PresentationActions", "ShowExport");
            break;
        case "player-settings":
            trackActivity("PresentationActions", "ShowPlayerSettings");
            break;
        case "analytics":
            trackActivity("PresentationAnalytics", "Open");
            trackActivity("PresentationActions", "ShowAnalytics");
            break;
        case "version-history":
            trackActivity("PresentationActions", "ShowRecoverSlides");
            break;
        case "properties":
            trackActivity("PresentationActions", "ShowProperties");
            break;
    }
}

class PresentationSettingsContainer extends Component {
    constructor(props) {
        super();

        this.state = {
            isOpen: true,
            selectedPaneId: props.startPane || "share"
        };
    }

    componentDidMount() {
        const { presentation } = this.props;
        const { selectedPaneId } = this.state;
        window.history.replaceState({}, "", `/${presentation.id}/settings/${selectedPaneId}`);

        window.addEventListener("popstate", this.handlePopState);
    }

    componentWillUnmount() {
        const { presentation } = this.props;

        window.removeEventListener("popstate", this.handlePopState);
        window.history.replaceState({}, "", `/${presentation.id}/${PresentationEditorController.getCurrentSlideIndex() + 1}`);
    }

    handlePopState = event => {
        const path = window.location.pathname;
        const pane = path.split("/").pop();

        if (path.includes("settings")) {
            this.setState({ selectedPaneId: pane });
        } else {
            this.props.closeDialog();
        }
    };

    handleBackClicked = () => {
        const currentSlideIndex = PresentationEditorController.getCurrentSlideIndex() + 1;

        if (this.state.selectedPaneId === "analytics" && AnalyticsController.showSelectedLink) {
            AnalyticsController.clearSelectedLink();
            this.forceUpdate();
        } else {
            this.setState({ isOpen: false });
        }

        window.history.replaceState({}, "", `/${this.props.presentation.id}/${currentSlideIndex}`);
    };

    handleReturnToLibrary = () => {
        this.props.closeDialog();
        AppController.showLibrary();
    }

    selectPane = pane => {
        trackAnalytics(pane);
        this.setState({ selectedPaneId: pane });
        window.history.pushState({}, "", `/${this.props.presentation.id}/settings/${pane}`);
    };

    renderSelectedPane = () => {
        const { presentation } = this.props;
        const { selectedPaneId } = this.state;

        switch (selectedPaneId || "share") {
            case "share":
                return <SharePane presentation={presentation} />;
            case "collaborate":
                return <CollaboratePane presentation={presentation} />;
            case "analytics":
                return <Analytics presentation={presentation} />;
            case "export":
                if (WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use) {
                    return <ExportPane presentation={presentation} />;
                } else {
                    this.setState({ selectedPaneId: "share" });
                    return <SharePane presentation={presentation} />;
                }
            case "player-settings":
                return <PlayerSettingsPane presentation={presentation} />;
            case "version-history":
                return <VersionHistoryPane presentation={presentation} />;
            case "properties":
                return <PropertiesPane presentation={presentation} handleViewAnalytics={() => this.selectPane("analytics")} />;
        }
    };

    render() {
        const { presentation } = this.props;
        const { isOpen, selectedPaneId } = this.state;

        return (
            <UIContainer
                isOpen={isOpen}
                className="presentation-settings"
                onClose={this.props.closeDialog}
                onBeforeClose={this.props.onBeforeClose}
            >
                <UINavigation
                    selected={selectedPaneId}
                    onSelect={id => {
                        this.selectPane(id);
                    }}
                    onBack={this.handleBackClicked}
                    fullHeight
                >
                    <UINavigationItem onCustomSelect={this.handleReturnToLibrary} id="return-to-library" title={t("Return to Library")} icon="list" />

                    <Divider color="#666" margin={20} />

                    <UINavigationItem id="share" title={t("Share")} icon="share" />
                    <UINavigationItem id="collaborate" title={t("Collaborate")} icon="people" />
                    <UINavigationItem
                        id="player-settings"
                        title={t("Player Settings")}
                        icon={!WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, "use") && "play_circle"}
                        image={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, "use") && <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} />}
                    />
                    <UINavigationItem
                        id="analytics"
                        title={t("Analytics")}
                        icon={!WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.ANALYTICS, "use") && "show_chart"}
                        image={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.ANALYTICS, "use") && <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} />}
                    />
                    {WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use &&
                        <UINavigationItem id="export" title={t("Export")} icon="cloud_download" />
                    }

                    <Divider color="#666" margin={20} />

                    <UINavigationItem id="version-history" title={t("Recover Slides")} icon="restore_from_trash" />

                    <Divider color="#666" margin={20} />

                    <UINavigationItem id="properties" title={t("Properties")} icon="info" />
                </UINavigation>

                {this.renderSelectedPane()}
            </UIContainer>
        );
    }
}

export default PresentationSettingsContainer;
